import { GET_SURVEY } from 'constants/api'
import { AuthError, UserCredential } from '@firebase/auth'
import useAxios, { RefetchOptions } from 'axios-hooks'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import { createContext, Dispatch, FunctionComponent, useContext, useEffect, useReducer } from 'react'
import { SurveyAdapter } from 'views/raffles/types'
import sessionReducer, { SessionAction, SessionState, UserData } from './session-reducer'

export interface UserContextProps {
  state: SessionState
  dispatch: Dispatch<SessionAction>
  refetch: (config?: AxiosRequestConfig, options?: RefetchOptions) => AxiosPromise<any>
}

const SessionContext = createContext<UserContextProps>({} as UserContextProps)

export const SessionProvider: FunctionComponent = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, {})
  const [{ response, loading }, refetch] = useAxios(
    {
      url: GET_SURVEY + SURVEY_TYPE,
      headers: { Authorization: `Bearer ${((state.data as any)?._tokenResponse as any)?.idToken}` }
    },
    {
      manual: true
    }
  )

  useEffect(() => {
    if (state.data && !response?.status) {
      refetch()
    }
  }, [state.data])

  useEffect(() => {
    if (response?.status === 200) {
      gtag('event', 'survey', {
        event_label: 'Survey - Incompleta',
        method: "Survey"
      })
      dispatch({ type: 'set_survey', payload: response.data?.map((survey) => SurveyAdapter(survey)) })
    }
    if (response?.status === 202) {
      gtag('event', 'survey', {
        event_label: 'Survey - Completa',
        method: "Survey"
      })
      dispatch({ type: 'set_survey', payload: [] })
      dispatch({ type: 'user', payload: response.data })
    }
  }, [response, loading])


  return <SessionContext.Provider value={{ state, dispatch, refetch }}>{children}</SessionContext.Provider>
}

export const goToLogin = () => {
  window.localStorage.removeItem('state')
  window.location.reload()
}

export const useSession = () => {
  const { state, dispatch, refetch } = useContext(SessionContext)

  useEffect(() => {
    const ls = localStorage.getItem('state')
    const data = ls ? JSON.parse(ls) : {}

    if (!Object.keys(data)) {
      goToLogin()
      return
    }

    if (data.data?.user?.stsTokenManager) {
      const expirationDate = data.data?.user?.stsTokenManager.expirationTime

      const isOutdated = Date.now() > expirationDate

      if (isOutdated) {
        goToLogin()
        return
      }
    }
    dispatch({ type: 'restore', payload: data })
  }, [dispatch])

  useEffect(() => {
    if (Object.keys(state).length) {
      localStorage.setItem('state', JSON.stringify({ user: state?.user, data: state?.data }))
    }
  }, [state])

  const setError = (error: AuthError) => dispatch({ type: 'error', payload: error })
  const setSession = (session: UserCredential) => dispatch({ type: 'success', payload: session })
  const setUser = (user: UserData) => dispatch({ type: 'user', payload: user })
  const setPoints = (points: number) => dispatch({ type: 'points', payload: points })
  const actions = { setError, setSession, setUser, setPoints, refetch }
  const returnValue: [typeof state, typeof actions] = [state, actions]

  return returnValue
}
